/**
 * Right Panel Component - Collapsible Sidebar
 *
 * Control panels:
 * - Emotion Presets Control (includes Body Actions)
 * - Eye Control
 * - Blink Control
 * - Mouth Control
 * - Body Color Control
 * - Background Video Control
 * - BGM Control
 * - Camera Control Panel
 */

import React, { Suspense, lazy, useState } from 'react';
import './RightPanel.css';

// Lazy load panels for better performance
const EmotionPresetsControl = lazy(() => import('@/hooks/react/components/EmotionPresetsControl.js'));
const EyeControl = lazy(() => import('@/hooks/react/components/EyeControl.js'));
const BlinkControl = lazy(() => import('@/hooks/react/components/BlinkControl.js'));
const MouthControl = lazy(() => import('@/hooks/react/components/MouthControl.js'));
const BodyColorControl = lazy(() => import('@/hooks/react/components/BodyColorControl.js'));
const BackgroundVideoControl = lazy(() => import('@/hooks/react/components/BackgroundVideoControl.js'));
const BGMControl = lazy(() => import('@/hooks/react/components/BGMControl.js'));
const CameraControlPanel = lazy(() => import('@/hooks/react/components/CameraControlPanel.js'));

const PanelFallback: React.FC = () => (
    <div className="panel-loading">読み込み中...</div>
);

// Toggle button icon
const ChevronIcon: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transform: direction === 'left' ? 'rotate(0deg)' : 'rotate(180deg)' }}
    >
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

export const RightPanel: React.FC = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    return (
        <div className={`right-panel control-panel sidebar ${isCollapsed ? 'sidebar--collapsed' : ''}`}>
            {/* Toggle Button */}
            <button
                className="sidebar-toggle sidebar-toggle--right"
                onClick={() => setIsCollapsed(!isCollapsed)}
                aria-label={isCollapsed ? 'サイドバーを開く' : 'サイドバーを閉じる'}
            >
                <ChevronIcon direction={isCollapsed ? 'left' : 'right'} />
            </button>

            {/* Sidebar Header */}
            <div className="sidebar-header">
                <h2 className="sidebar-title">Controls</h2>
            </div>

            {/* Panel Content */}
            <div className="panel-content sidebar-content">
                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <EmotionPresetsControl />
                    </div>
                </Suspense>

                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <EyeControl />
                    </div>
                </Suspense>

                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <BlinkControl />
                    </div>
                </Suspense>

                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <MouthControl />
                    </div>
                </Suspense>

                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <BodyColorControl />
                    </div>
                </Suspense>

                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <BackgroundVideoControl />
                    </div>
                </Suspense>

                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <BGMControl />
                    </div>
                </Suspense>

                <Suspense fallback={<PanelFallback />}>
                    <div className="panel-section">
                        <CameraControlPanel />
                    </div>
                </Suspense>
            </div>
        </div>
    );
};

export default RightPanel;
